import { BaseModel, Default } from '@red/data-access';
import { Expose, Transform, Type } from 'class-transformer';
import { UpdatedByModel } from './updated-by.model';
import { ECDD2_TRANSACTION_TYPE_OPTIONS, ECDD_ACTING_TYPE_OPTIONS, ECDD_FILE_NAME_TYPE_OPTIONS, ECDD_FILE_TYPE_OPTIONS, ECDD_PROPERTY_TYPE_OPTIONS, ECDD_REPRESENT_TYPE_OPTIONS, ECDD_STATUSES, ECDD_TRANSACTION_TYPE_OPTIONS, UCP_ECDD_ACTING_TYPE_OPTIONS } from '../data/ecdd.data';
import { RESALE_PROPERTY_TYPE_OPTIONS } from '../data/resale.data';
import { EEcdd2TransactionType, EEcddActingType, EEcddClientType, EEcddFormCAndU6FileNameType, EEcddPropertyType, EEcddRepresentType, EEcddStatus, EEcddTransactionType, EEcddType } from '../enums/ecdd.enum';
import { EResalePropertyType } from '../enums/resale.enum';
import { IEcdd, IQuickAmlScreening } from '../interfaces/ecdd.interface';
import { IStatusDescription } from '../interfaces/status-description.interface';
import { EcddCustomerModel } from './ecdd-customer.model';
import { EcddOptionGroupModel } from './ecdd-option-group.model';
import { PersonalParticularModel } from './personal-particular.model';
import { AddressModel } from './address.model';
import { IAmlClient } from '@shared/components/ingenique-api';
import { UnitModel } from './unit.model';
import { ProjectModel } from './project.model';
import { EEcddFileAMLReportSet, EEcddFormCheckListExcludeSet, EEcddFormMultipleSupportDocumentsSet, EEcddFormSupportDocumentsSet, EEcddMainCustomerSet, EEcddFileACRAReportSet } from '../constants';

export class ECDDMasteModel extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  masterEcddId!: number;

  @Expose()
  code!: string;

  @Expose()
  redocId!: number; // id of ecdd master data from redoc

  @Expose()
  transactionType!: EEcdd2TransactionType;

  @Expose()
  propertyType!: string;

  @Expose()
  propertyTypeOtherDescription!: string;

  @Expose()
  propertyId!: number;

  @Expose()
  projectId!: string;

  @Expose()
  unitId!: string;

  @Expose()
  projectName!: string;

  @Expose()
  unitNo!: string;

  @Expose()
  projectUnitNameOther?: string;

  @Expose()
  @Type(() => ProjectModel)
  project!: ProjectModel;

  @Expose()
  @Type(() => UnitModel)
  unit?: UnitModel;

  @Expose()
  address?: AddressModel;

  @Expose()
  metadata?: string;

  @Expose()
  ecdds!: EcddModel[];

  @Expose()
  key!: string;

  @Expose()
  @Default(true)
  expanded!: boolean;

  @Expose()
  totalCounts!: number;

  @Expose()
  isCanUpdate!: boolean;

  @Expose()
  createdBy!: PersonalParticularModel | null | number;

  @Expose()
  isAttachmentProcessing!: boolean;

  @Expose()
  isUsedByTransaction!: boolean;

  @Expose()
  @Default(false)
  get canEditInfoProject(): boolean {
    return this.isCanUpdate && this.transactionType === EEcdd2TransactionType.PROJECTSALE;
  }

  @Expose()
  @Default(false)
  get canEditPropertyAddress(): boolean {
    return this.isCanUpdate && this.transactionType === EEcdd2TransactionType.RESALE;
  }

  @Expose()
  get displayPropertyType() {
    if (this.transactionType === EEcdd2TransactionType.RESALE) {
      return this.propertyType ? RESALE_PROPERTY_TYPE_OPTIONS[this.propertyType as EResalePropertyType]?.['viewPropertyTypeDcdd'] ?? ECDD_PROPERTY_TYPE_OPTIONS[this.propertyType as EEcddPropertyType]?.viewValue : '';

    } else return ECDD_PROPERTY_TYPE_OPTIONS[this.propertyType as EEcddPropertyType]?.viewValue ?? '';
  }

  @Expose()
  get displayTransactionType() {
    return ECDD2_TRANSACTION_TYPE_OPTIONS[this.transactionType as EEcdd2TransactionType]?.viewValue ?? '';
  }

  @Expose()
  get displayUnit() {
    return (this.unitNo ? this.unitNo : this.projectUnitNameOther) ?? '';
  }

}

export class ECDDFileModel extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  ecddId!: number;

  @Expose()
  customerId!: number;

  @Expose()
  uuid!: string; // uuid of customer

  @Expose()
  type!: string;

  @Expose()
  name!: string;

  @Expose()
  fileUrl!: string;

  @Expose()
  file_type!: string;

  @Expose()
  attrs!: any;

  @Expose()
  redocFileId!: number;

  @Expose()
  redocId!: number;

  @Expose()
  docType!: string;

  @Expose()
  @Transform(({ obj }) => {
    return ECDD_FILE_NAME_TYPE_OPTIONS[obj.attrs.formCSupportingDocuments as EEcddFormCAndU6FileNameType]?.viewValue;
  })
  displayFileTypeName!: string;

  @Expose()
  sequence!: number;

  @Expose()
  @Transform(({ obj }) => {
    return ECDD_FILE_TYPE_OPTIONS[obj?.attrs?.docType ?? '']?.viewValue;
  })
  displayType!: string;

}
export class EcddModel extends BaseModel implements IEcdd {
  @Expose()
  id!: number;

  @Expose()
  redocId!: number;

  @Expose()
  sequence!: number;

  @Expose()
  code!: string;

  @Expose()
  @Default(EEcddStatus.draft)
  status!: EEcddStatus;

  @Expose()
  @Transform(({ obj }) => {
    return ECDD_STATUSES[obj.status as EEcddStatus];
  })
  displayStatus!: IStatusDescription;

  @Expose()
  type!: EEcddType;

  @Expose()
  @Default(EEcddTransactionType.Rental)
  transactionType!: EEcddTransactionType;

  @Expose()
  documentTitle!: string;

  @Expose()
  salespersonId!: number;

  @Expose()
  @Type(() => PersonalParticularModel)
  salesperson!: PersonalParticularModel;

  @Expose()
  signature!: string;

  @Expose()
  checklistAt!: string;

  @Expose()
  submittedAt!: string;

  @Expose()
  submittedBy!: any;

  @Expose()
  reviewedAt!: string;

  @Expose()
  reviewedBy!: any;

  @Expose()
  reviewed!: any;

  @Expose()
  completedAt!: string;

  @Expose()
  isUsedBy!: string;

  @Expose()
  @Type(() => AddressModel)
  address?: AddressModel;

  @Expose()
  isManually!: boolean;

  @Expose()
  @Type(() => EcddCustomerModel)
  @Default([])
  customers!: EcddCustomerModel[];

  @Expose()
  @Type(() => EcddCustomerModel)
  @Transform(({ obj }) => {
    return EcddCustomerModel.fromJson(obj.customers?.find((e: EcddCustomerModel) => EEcddMainCustomerSet.has(e.formType)) ?? obj.customers?.[0]);
  })
  mainCustomer!: EcddCustomerModel;

  @Expose()
  amlClient!: any;

  @Expose()
  kycSearchResult!: IAmlClient;

  @Expose()
  @Type(() => EcddOptionGroupModel)
  @Default([])
  ecddOptionGroups!: EcddOptionGroupModel[];

  //#region timestamps
  @Expose()
  createdAt!: string | null;

  @Expose()
  updatedAt!: string | null;

  @Expose()
  deletedAt!: string | null;

  @Expose()
  createdBy!: PersonalParticularModel | null;

  @Expose()
  updatedBy!: UpdatedByModel | null;

  @Expose()
  deletedBy!: UpdatedByModel | null;
  //#endregion

  @Expose()
  metadata!: {
    reason?: string;
    rejectedFile?: string;
    approvedFile?: string;
    transactionInfor?: { resaleCode: string };
  };

  @Expose()
  remarks!: string;

  @Expose()
  agentRemarks!: string;

  isParent!: boolean;

  @Expose()
  get clientDisplay() {
    return this.type === EEcddType.Individual || this.type === EEcddType.INDIVIDUAL || this.type === EEcddType.UCPINDIVIDUAL
      ? `${this.mainCustomer?.name ?? ''} ${this.mainCustomer?.nricfin ? '(' + this.mainCustomer?.nricfin + ')' : ''}`
      : `${this.mainCustomer?.name ?? ''} ${this.mainCustomer?.companyRegistrationNumber ? '(' + this.mainCustomer?.companyRegistrationNumber + ')' : ''}`;
  }

  @Expose()
  get salespersonDisplay() {
    return this.salesperson ? this.salesperson?.businessName + ' (' + this.salesperson?.ceaRegNo + ')' : null;
  }

  @Expose()
  get transactionTypeDisplay() {
    return ECDD_TRANSACTION_TYPE_OPTIONS[this.transactionType]?.viewValue ?? '';
  }

  @Expose()
  get statusDescription() {
    return ECDD_STATUSES[this.status];
  }

  @Expose()
  get canShowStatusReason(): boolean {
    return [EEcddStatus.rejected, EEcddStatus.approved].includes(this.status);
  }

  @Expose()
  get canShowAMLfile(): boolean {
    return [EEcddStatus.submitted, EEcddStatus.approved, EEcddStatus.rejected, EEcddStatus.clientSigned, EEcddStatus.autoApproved].includes(this.status);
  }

  @Expose()
  get canShowStatusFile(): boolean {
    // return (!!this.metadata?.approvedFile && this.status === EEcddStatus.approved) || (!!this.metadata?.rejectedFile && this.status === EEcddStatus.rejected);
    return !!this.metadata?.approvedFile || !!this.metadata?.rejectedFile;
  }
  @Expose()
  pdfUrl!: string;

  @Expose()
  uuid!: string;

  @Expose()
  isOld!: boolean;

  @Expose()
  key!: string;

  @Expose()
  @Default(true)
  expanded!: boolean;

  @Expose()
  totalCounts!: number;

  //new fields for DCDD

  @Expose()
  @Transform(({ obj }) => {
    if (obj.type === EEcddType.INDIVIDUAL || obj.type === EEcddType.ENTITY) {
      return EEcddClientType.Represented

    } else if (obj.type === EEcddType.UCPINDIVIDUAL || obj.type === EEcddType.UCPENTITY) {
      return EEcddClientType.Unrepresented;
    } else return '';
  })
  clientType!: string;

  @Expose()
  version!: 'dcdd' | 'ecdd';

  @Expose()
  @Default(false)
  @Transform(({ obj }) => {
    return obj.version === 'ecdd';
  })
  isECDD!: boolean;

  @Expose()
  propertyType!: EEcddPropertyType;

  @Expose()
  propertyTypeOther!: string;

  @Expose()
  masterEcddId!: number;

  @Expose()
  @Type(() => ECDDMasteModel)
  masterEcdd!: ECDDMasteModel;

  @Expose()
  representedType!: EEcddRepresentType;

  @Expose()
  clientAgentRel!: string;

  @Expose()
  clientActingType!: EEcddActingType;

  @Expose()
  @Type(() => ECDDFileModel)
  @Default([])
  ecddFiles!: ECDDFileModel[];

  @Expose()
  @Type(() => ECDDFileModel)
  @Default([])
  @Transform(({ obj }) => {
    return obj.ecddFiles?.filter(
      (e: ECDDFileModel) => EEcddFormSupportDocumentsSet.has(e.attrs?.docType)
    )
  })
  ecddFilesOther!: ECDDFileModel[];

  @Expose()
  @Type(() => ECDDFileModel)
  @Default([])
  @Transform(({ obj }) => {
    return obj.ecddFiles?.filter(
      (e: ECDDFileModel) => !EEcddFormCheckListExcludeSet.has(e.attrs?.docType)
    )
  })
  ecddFilesForm!: ECDDFileModel[];

  @Expose()
  @Type(() => ECDDFileModel)
  @Default([])
  @Transform(({ obj }) => {
    return obj.ecddFiles?.filter(
      (e: ECDDFileModel) => EEcddFormMultipleSupportDocumentsSet.has(e.attrs?.docType)
    )
  })
  ecddFilesSupportCAndU6!: ECDDFileModel[];


  @Expose()
  @Type(() => ECDDFileModel)
  @Default([])
  @Transform(({ obj }) => {
    return obj.ecddFiles?.filter(
      (e: ECDDFileModel) => EEcddFileAMLReportSet.has(e.attrs?.docType)
    )
  })
  ecddFilesAMLReport!: ECDDFileModel[];

  @Expose()
  @Type(() => ECDDFileModel)
  @Default([])
  @Transform(({ obj }) => {
    return obj.ecddFiles?.filter(
      (e: ECDDFileModel) => EEcddFileACRAReportSet.has(e.attrs?.docType)
    )
  })
  ecddFilesACRA!: ECDDFileModel[];

  @Expose()
  @Transform(({ obj }) => {
    return obj.type === EEcddType.INDIVIDUAL || obj.type === EEcddType.ENTITY ? ECDD_ACTING_TYPE_OPTIONS[obj.clientActingType as EEcddActingType]?.viewValue : UCP_ECDD_ACTING_TYPE_OPTIONS[obj.clientActingType as EEcddActingType]?.viewValue;
  })
  displayActingType!: string;

  @Expose()
  @Transform(({ obj }) => {
    return ECDD_REPRESENT_TYPE_OPTIONS[obj.representedType as EEcddRepresentType]?.viewValue;
  })
  displayRepresentedType!: string;

  @Expose()
  get transactionCode() {
    return this.isUsedBy;
  }

  @Expose()
  @Transform(({ obj }) => {
    let clientType = '';
    const mainCustomer = obj?.version === 'dcdd' ? obj.customers?.find((e: EcddCustomerModel) => e?.isMainCustomer) : obj.customers?.[0];
    if (obj.type === EEcddType.INDIVIDUAL || obj.type === EEcddType.ENTITY) {
      clientType = 'REP'

    } else if (obj.type === EEcddType.UCPINDIVIDUAL || obj.type === EEcddType.UCPENTITY) {
      clientType = 'UCP';
    } else clientType = '';
    return (clientType ? clientType + ' - ' : '') + (mainCustomer?.name ? '(' + mainCustomer?.name?.toUpperCase() + ') ' : '') + obj.code;
  })
  displayNameForTransaction!: string;


  @Expose()
  get canRefreshStatus(): boolean {
    return [EEcddStatus.attachmentProcessing].includes(this.status);
  }

  isRecentlyUpdated!: boolean;

  @Expose()
  @Default([])
  quickAmlScreenings!: IQuickAmlScreening[];

}

export class EcddForTransactionModel extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  code!: string;

  @Expose()
  type!: string;

  @Expose()
  isECDD!: boolean;
}




export class EcddHipModel extends BaseModel {
  @Expose()
  id!: number;

  @Expose()
  code!: string;

  @Expose()
  name!: string;

  @Expose()
  nric!: string;

  @Expose()
  clientType!: string;

  @Expose()
  customerType!: string;

  @Expose()
  status!: string;

  @Expose()
  transactionType!: string;

  @Expose()
  referenceNumber!: string;

  @Expose()
  submissionDate!: string;

  @Expose()
  dateApproved!: string | null;

  @Expose()
  approvedBy!: string | null;

  @Expose()
  createdByNric!: string;

  @Expose()
  updatedByNric!: string;

  @Expose()
  salespersonId!: number | null;

  @Expose()
  isUsedBy!: string | null;

  @Expose()
  @Default('ecdd_hip')
  version!: string;

  @Expose()
  type!: string;

  @Expose()
  createdAt!: string;

  @Expose()
  updatedAt!: string;


  @Expose()
  @Transform(({ obj }) => {
    const name = (obj?.referenceNumber ?? '') + (obj?.name ? '_' + obj?.name?.toUpperCase() : '') + (obj?.nric ? '_' + obj?.nric : '');
    return name;
  })
  displayNameForTransaction!: string;
}